import React, { useRef, useState, useMemo, useCallback, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { List, ListImperativeAPI, RowComponentProps } from 'react-window';
import './domainsTable.scss';
import { DomainAndSource } from '../../crawler.interface';
import { validateDomain, validateTrafficSource } from '../../../utils/utils';

const ROW_HEIGHT = 36;
const MAX_LIST_HEIGHT = 400;

export interface DomainsTableBackendError {
    index: number;
    msg: string;
}

export interface DomainsTableProps {
    rows: DomainAndSource[];
    onChangeRow: (index: number, field: 'domain' | 'source', value: string) => void;
    overriddenRows: Set<number>;
    onToggleOverride: (index: number) => void;
    backendErrorRow: DomainsTableBackendError | null;
    autoLowercasedCount?: number;
}

interface RowValidation {
    domainErr: string | null;
    sourceErr: string | null;
}

type DomainRowProps = {
    rows: DomainAndSource[];
    validations: RowValidation[];
    overriddenRows: Set<number>;
    backendErrorRow: DomainsTableBackendError | null;
    visibleIndices: number[];
    onChangeRow: (index: number, field: 'domain' | 'source', value: string) => void;
    onToggleOverride: (index: number) => void;
};

function validateRow(row: DomainAndSource): RowValidation {
    return {
        domainErr: validateDomain(row.domain),
        sourceErr: validateTrafficSource(row.source),
    };
}

function ErrorHoverTooltip({ text, className, testId }: { text: string; className: string; testId?: string }) {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 0 });
    const anchorRef = useRef<HTMLSpanElement>(null);

    const show = useCallback(() => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        setCoords({ left: rect.left, top: rect.bottom + 6, width: rect.width });
        setVisible(true);
    }, []);
    const hide = useCallback(() => setVisible(false), []);

    useEffect(() => {
        if (!visible) return;
        const onScroll = () => setVisible(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [visible]);

    return (
        <>
            <span
                ref={anchorRef}
                className={className}
                tabIndex={0}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                data-testid={testId}
            >
                {text}
            </span>
            {visible && createPortal(
                <div
                    className="domains-table-error-tooltip"
                    role="tooltip"
                    style={{ left: coords.left, top: coords.top, maxWidth: Math.max(coords.width, 320) }}
                >
                    {text}
                </div>,
                document.body
            )}
        </>
    );
}

function RowRenderer({
    index,
    style,
    rows,
    validations,
    overriddenRows,
    backendErrorRow,
    visibleIndices,
    onChangeRow,
    onToggleOverride,
}: RowComponentProps<DomainRowProps>) {
    const actualIndex = visibleIndices[index];
    const row = rows[actualIndex];
    const { domainErr, sourceErr } = validations[actualIndex];
    const overridden = overriddenRows.has(actualIndex);
    const hasFormatErr = !!(domainErr || sourceErr);
    const isBackendErr = backendErrorRow?.index === actualIndex;

    const rowClasses = ['domains-row'];
    if (isBackendErr) rowClasses.push('backend-error');
    else if (hasFormatErr && !overridden) rowClasses.push('has-error');
    if (overridden && hasFormatErr) rowClasses.push('overridden');

    return (
        <div className={rowClasses.join(' ')} style={style} data-testid={`domains-row-${actualIndex}`}>
            <span className="col-index">{actualIndex + 1}</span>
            <input
                className={'col-url' + (domainErr && !overridden ? ' input-error' : '')}
                type="text"
                value={row.domain}
                onChange={(e) => onChangeRow(actualIndex, 'domain', e.target.value)}
                data-testid={`domains-row-${actualIndex}-url`}
            />
            <input
                className={'col-source' + (sourceErr && !overridden ? ' input-error' : '')}
                type="text"
                value={row.source}
                onChange={(e) => onChangeRow(actualIndex, 'source', e.target.value)}
                data-testid={`domains-row-${actualIndex}-source`}
            />
            <div className="col-status">
                {isBackendErr ? (
                    <ErrorHoverTooltip
                        text={backendErrorRow.msg}
                        className="status-backend-err"
                        testId={`domains-row-${actualIndex}-backend-err`}
                    />
                ) : hasFormatErr ? (
                    <>
                        <ErrorHoverTooltip
                            text={domainErr || sourceErr || ''}
                            className="status-err-text"
                            testId={`domains-row-${actualIndex}-format-err`}
                        />
                        <label className="verify-anyway">
                            <input
                                type="checkbox"
                                checked={overridden}
                                onChange={() => onToggleOverride(actualIndex)}
                                data-testid={`domains-row-${actualIndex}-override`}
                            />
                            Verify anyway
                        </label>
                    </>
                ) : (
                    <span className="status-ok">OK</span>
                )}
            </div>
        </div>
    );
}

const Row = memo(RowRenderer, (prev, next) => {
    if (prev.index !== next.index) return false;
    if (prev.style.top !== next.style.top || prev.style.height !== next.style.height) return false;
    const pActual = prev.visibleIndices[prev.index];
    const nActual = next.visibleIndices[next.index];
    if (pActual !== nActual) return false;
    const i = nActual;
    const pBe = prev.backendErrorRow?.index === i;
    const nBe = next.backendErrorRow?.index === i;
    if (pBe !== nBe) return false;
    if (pBe && nBe && prev.backendErrorRow!.msg !== next.backendErrorRow!.msg) return false;
    return (
        prev.rows[i] === next.rows[i]
        && prev.validations[i] === next.validations[i]
        && prev.overriddenRows.has(i) === next.overriddenRows.has(i)
        && prev.onChangeRow === next.onChangeRow
        && prev.onToggleOverride === next.onToggleOverride
    );
});
Row.displayName = 'DomainsTableRow';

export default function DomainsTable({
    rows,
    onChangeRow,
    overriddenRows,
    onToggleOverride,
    backendErrorRow,
    autoLowercasedCount = 0,
}: DomainsTableProps) {
    const validations = useMemo(() => rows.map(validateRow), [rows]);

    const errorIndices = useMemo(() => {
        const set = new Set<number>();
        validations.forEach((v, i) => {
            if (!overriddenRows.has(i) && (v.domainErr || v.sourceErr)) set.add(i);
        });
        if (backendErrorRow) set.add(backendErrorRow.index);
        return [...set].sort((a, b) => a - b);
    }, [validations, overriddenRows, backendErrorRow]);
    const errorCount = errorIndices.length;
    const passingCount = rows.length - errorCount;

    const [showAll, setShowAll] = useState<boolean>(false);

    // Errors always visible at the top (in original CSV order). Passing rows appear below
    // only when the user opts in — keeps the "review failures" case uncluttered.
    const visibleIndices = useMemo(() => {
        if (!showAll) return errorIndices;
        const errorSet = new Set(errorIndices);
        const passing: number[] = [];
        for (let i = 0; i < rows.length; i++) {
            if (!errorSet.has(i)) passing.push(i);
        }
        return [...errorIndices, ...passing];
    }, [showAll, errorIndices, rows.length]);

    const listRef = useRef<ListImperativeAPI | null>(null);
    const [lastJumpedDisplayIndex, setLastJumpedDisplayIndex] = useState<number>(-1);

    const jumpToNextViolation = () => {
        if (errorIndices.length === 0) return;
        const errorSet = new Set(errorIndices);
        const errorDisplayPositions: number[] = [];
        visibleIndices.forEach((actualIdx, displayIdx) => {
            if (errorSet.has(actualIdx)) errorDisplayPositions.push(displayIdx);
        });
        if (errorDisplayPositions.length === 0) return;
        const next = errorDisplayPositions.find(p => p > lastJumpedDisplayIndex) ?? errorDisplayPositions[0];
        setLastJumpedDisplayIndex(next);
        listRef.current?.scrollToRow({ index: next, align: 'smart', behavior: 'smooth' });
    };

    const rowProps = useMemo<DomainRowProps>(() => ({
        rows,
        validations,
        overriddenRows,
        backendErrorRow,
        visibleIndices,
        onChangeRow,
        onToggleOverride,
    }), [rows, validations, overriddenRows, backendErrorRow, visibleIndices, onChangeRow, onToggleOverride]);

    const listHeight = Math.min(visibleIndices.length * ROW_HEIGHT, MAX_LIST_HEIGHT);
    const canToggle = passingCount > 0;

    return (
        <div className="domains-table-wrapper" data-testid="domains-table">
            {autoLowercasedCount > 0 && (
                <div className="domains-table-info-banner" data-testid="auto-lowercased-banner">
                    {autoLowercasedCount} URL scheme{autoLowercasedCount === 1 ? ' was' : 's were'} auto-lowercased to match backend expectations.
                </div>
            )}
            <div className="domains-table-header-row">
                <span className="domains-table-title">Uploaded Domains ({rows.length})</span>
                <div className="domains-table-controls">
                    {errorCount > 0 && (
                        <>
                            <span className="domains-table-error-count">
                                {errorCount} row{errorCount === 1 ? '' : 's'} need attention
                            </span>
                            <button
                                type="button"
                                className="jump-to-next-btn"
                                onClick={jumpToNextViolation}
                                data-testid="jump-to-next-violation"
                            >
                                Jump to next &darr;
                            </button>
                        </>
                    )}
                    {canToggle && (
                        <button
                            type="button"
                            className="toggle-show-all-btn"
                            onClick={() => setShowAll(prev => !prev)}
                            data-testid="toggle-show-all-domains"
                        >
                            {showAll
                                ? `Hide ${passingCount} passing`
                                : errorCount > 0
                                    ? `Show all ${rows.length} domains`
                                    : `Click to see all ${rows.length} domains`}
                        </button>
                    )}
                </div>
            </div>

            <div className="domains-table">
                <div className="domains-row domains-row-header">
                    <span className="col-index">#</span>
                    <span className="col-url">URL</span>
                    <span className="col-source">Traffic Source</span>
                    <span className="col-status">Status</span>
                </div>

                {visibleIndices.length > 0 ? (
                    <List
                        listRef={listRef}
                        rowComponent={Row}
                        rowCount={visibleIndices.length}
                        rowHeight={ROW_HEIGHT}
                        rowProps={rowProps}
                        overscanCount={5}
                        style={{ height: listHeight }}
                    />
                ) : (
                    <div className="domains-table-empty-state" data-testid="domains-table-empty-state">
                        All {rows.length} domain{rows.length === 1 ? '' : 's'} passed validation.
                    </div>
                )}
            </div>
        </div>
    );
}
